import type {
    BarSeriesOption,
    EChartsOption,
    GraphSeriesOption,
    LegendComponentOption,
    LineSeriesOption,
    PieSeriesOption,
    SeriesOption,
    WordCloudSeriesOption,
} from "echarts";
import { GridOption } from "echarts/types/dist/shared";
import { getPercentage } from "./commonUtils";

export interface ChartOption {
    data: ChartData;
    layout: LayoutOption;
}

export interface ChartData {
    title: string;
    data: {
        name: string;
        value: number[] | number;
        chartType?: "line" | "bar";
        symbol?: "circle" | "rect" | "emptyRect" | "triangle" | "diamond" | "pin" | "arrow";
        symbolSize?: number;
        stack?: string;
    }[];
    links?: { source: string; target: string }[];
    xAxis?: string[];
    isEmpty?: boolean;
}
export interface ChartSort {
    name: string;
    valueKey: string;
    valueIdx?: number;
    chartType?: "line" | "bar";
    symbol?: "circle" | "rect" | "emptyRect" | "triangle" | "diamond" | "pin" | "arrow";
    stack?: string;
}

export interface LayoutOption {
    type: "line" | "bar" | "pie" | "donut" | "wordCloud" | "coOccurrence" | "percent";
    title: { text: string; sub?: string; position?: "center" | "left" | "right" };

    xAxisKey: string;
    legendPosition?: "left" | "right" | "bottom" | "top";

    isRowChart?: boolean;
    grid?: GridOption;

    valueUnit?: string; // 값 단위

    useLegend?: boolean;
    useToolbox?: boolean;
    useScroll?: boolean;

    wordCloudShape?: WordcloudShape; // 워드 클라우드 모양
}

type WordcloudShape = "circle" | "cardioid" | "diamond" | "triangle" | "pentagon" | "star";

export const setOption = (layout: LayoutOption, data: ChartData): EChartsOption => {
    const chart: EChartsOption = {
        grid: { ...setDefaultGrid(layout), ...layout.grid },
        ...setLayout(layout, data),
        series: setData(layout, data),
        ...(data.isEmpty
            ? {
                  graphic: [
                      {
                          type: "text",
                          left: "center",
                          top: "middle",
                          style: {
                              text: "데이터가 없습니다",
                              fill: "#999",
                              fontSize: 14,
                          },
                      },
                  ],
              }
            : {}),
    };

    return chart;
};

// chart series(차트에 보여주는 값) 세팅
const setData = (layout: LayoutOption, data: ChartData): SeriesOption[] => {
    switch (layout.type) {
        case "line":
            return data.data.map<SeriesOption>((d) => {
                const type = (d.chartType ?? layout.type) as "line" | "bar";

                return {
                    type: type,
                    name: d.name,
                    data: !data.isEmpty ? d.value : 0,
                    ...(d.symbol ? { symbol: d.symbol, symbolSize: 7 } : { symbolSize: 5 }),
                } as LineSeriesOption;
            });
        case "bar":
            return data.data.map<SeriesOption>((d) => {
                const type = (d.chartType ?? layout.type) as "line" | "bar";

                return {
                    type: type,
                    name: d.name,
                    data: !data.isEmpty ? d.value : 0,
                    ...(d.symbol ? { symbol: d.symbol, symbolSize: 7 } : { symbolSize: 5 }),
                } as BarSeriesOption;
            });
        case "percent":
            const value = data.data.map((item) => (Array.isArray(item.value) ? item.value[0] : item.value));
            const percentage = getPercentage(value);
            return data.data.map<SeriesOption>((d, idx) => {
                return {
                    type: "bar",
                    name: d.name,
                    stack: "total",
                    label: {
                        show: true,
                        formatter: (value) => (value.value ? `${value.value.toLocaleString()}${layout.valueUnit}` : ""),
                    },
                    data: !data.isEmpty ? [percentage[idx]] : [],
                } as BarSeriesOption;
            });

        case "pie":
        case "donut": {
            const pieData = data.data.map((d) => ({
                name: d.name,
                value: Array.isArray(d.value) ? d.value.reduce((a, b) => a + b, 0) : d.value,
            }));

            const s: PieSeriesOption = {
                type: "pie",
                data: !data.isEmpty ? pieData : [],
                radius: layout.type === "donut" ? ["40%", "70%"] : "70%",
            };

            return [s];
        }

        case "wordCloud": {
            return [
                {
                    type: "wordCloud",
                    shape: "circle" as WordcloudShape,
                    sizeRange: [15, 45],
                    rotationRange: [0, 45],
                    rotationStep: 5,
                    gridSize: 5,
                    textStyle: {
                        color: () =>
                            `rgb(${[Math.round(Math.random() * 160), Math.round(Math.random() * 160), Math.round(Math.random() * 160)].join(",")})`,
                    },
                    emphasis: { focus: "series" },
                    drawOutOfBound: false,
                    data: data.data,
                },
            ] as WordCloudSeriesOption[];
        }
        case "coOccurrence": {
            return [
                {
                    type: "graph",
                    layout: "force",
                    data: data.data,
                    links: data.links,
                    draggable: true,
                    roam: true,
                    zoom: 1,
                    label: {
                        show: true,
                        fontSize: 15,
                    },
                    force: {
                        repulsion: 150,
                        edgeLength: [80, 120],
                        gravity: 0.1,
                    },
                    lineStyle: { width: 1, opacity: 0.6 },
                },
            ] as GraphSeriesOption[];
        }

        default:
            return [];
    }
};

// 차트 타이틀, 범례, x축, y축 등 레이아웃 세팅
const setLayout = (options: LayoutOption, data: ChartData): EChartsOption => {
    const layout: EChartsOption = {
        title: {
            show: true,
            text: data.title ? data.title : options.title?.text ? options.title?.text : "",
            subtext: options.title?.sub ?? "",
            left: options.title?.position ?? "left",
        },

        legend: setLegend(options),
        // ...(options.useScroll ? { dataZoom: [{ type: "inside", xAxisIndex: 0 }] } : {}),

        ...setAxis(options, data),
        ...setToolbox(options),
        ...setTooltip(options, data),
    };

    return layout;
};

// 차트에 다운로드, 차트 초기화 등 기타 기능 세팅
const setToolbox = (option: LayoutOption): EChartsOption => {
    return {
        toolbox: {
            show: option.useToolbox,
            feature: {
                restore: { show: true },
                saveAsImage: { show: true },
                // magicType: { show: true, type: ["stack", "line", "bar"] },
            },
        },
    };
};

// 차트에 마우스 올렸을때 나오는 tooltip 세팅
const setTooltip = (option: LayoutOption, data: ChartData): EChartsOption => {
    return {
        tooltip: {
            trigger: option.type === "line" || option.type === "bar" ? "axis" : "item",
            valueFormatter: (value) => {
                if (option.valueUnit) {
                    if (option.type === "percent") {
                        return `${value?.toLocaleString()} ${option.valueUnit}`;
                    } else return `${value?.toLocaleString()} ${option.valueUnit}`;
                } else return `${value}`;
            },
        },
    };
};

// 차트의 위치 세팅 - 차트 영역의 padding 조절
const setDefaultGrid = (option: LayoutOption): GridOption => {
    let grid = { left: "8%", right: "5%", bottom: "8%" } as GridOption;

    switch (option.legendPosition) {
        case "top":
            grid = {
                left: "5%",
                right: "5%",
                top: "15%",
            };
            break;
        case "right":
            grid = {
                left: "5%",
                right: "8%",
                bottom: "8%",
            };
            break;
        case "bottom":
            grid = {
                left: "5%",
                right: "5%",
                bottom: "15%",
            };
            break;
        case "left":
            grid = {
                left: "8%",
                right: "5%",
                bottom: "8%",
            };
            break;
        default:
            grid = {
                ...grid,
                containLabel: true,
                ...option.grid,
            };
            break;
    }
    return grid;
};

// 범례 위치 설정
const setLegend = (option: LayoutOption): LegendComponentOption => {
    let legend = { left: 0, top: "center", orient: "vertical" } as LegendComponentOption;

    switch (option.legendPosition) {
        case "top":
            legend = {
                top: 0,
                orient: "horizontal",
            };
            break;
        case "right":
            legend = {
                right: 0,
                top: "center",
                orient: "vertical",
            };
            break;
        case "bottom":
            legend = {
                bottom: 0,
                orient: "horizontal",
            };
            break;
        case "left":
            legend = {
                left: 0,
                top: "center",
                orient: "vertical",
            };
            break;

        default:
            legend = {
                ...legend,
                show: option.useLegend ?? true,
            };
            break;
    }

    return legend;
};

const setAxis = (options: LayoutOption, data: ChartData): EChartsOption => {
    if (options.type === "line" || options.type === "bar") {
        if (options.type === "bar" && options.isRowChart) {
            return {
                xAxis: {
                    type: "value",
                    minInterval: 1,
                    axisLabel: options.valueUnit ? { formatter: `{value} ${options.valueUnit}` } : {},
                },
                yAxis: {
                    type: "category",
                    data: data.xAxis,
                },
            };
        } else
            return {
                xAxis: [
                    {
                        type: "category",
                        data: data.xAxis,
                    },
                ],
                yAxis: {
                    type: "value",
                    minInterval: 1,
                    axisLabel: options.valueUnit ? { formatter: `{value} ${options.valueUnit}` } : {},
                },
            };
    } else if (options.type === "percent") {
        return {
            xAxis: {
                type: "value",
                max: data.data.reduce((sum: number, item) => sum + (item.value as number[])[0], 0),
                minInterval: 1,
                axisLabel: options.valueUnit ? { formatter: `{value}${options.valueUnit}` } : {},
                position: "bottom",
            },
            yAxis: {
                type: "category",
                data: [""],
            },
        };
    } else {
        return {};
    }
};
